import { Box, Button, Divider, styled, Typography } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { FC, useEffect, useMemo } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Link, useParams } from 'react-router-dom'

import { useVideosTraining } from '@/api/videos/get-training'
import LogoText from '@/assets/images/logo-stay-safe.svg'
import trainingCompleteAnimation from '@/assets/lottie/completedTraining.json'
import trainingPackageCompleteAnimation from '@/assets/lottie/victoryCup.json'
import useDeviceDetection, { DeviceType } from '@/common/hooks/useDeviceDetection'
import useLottie from '@/common/hooks/useLottie'
import { useUrlSearchQuery } from '@/common/hooks/useUrlQuery'
import { theme } from '@/theme/theme'
import VideosCarousel from '../TrainingCampaignView/VideosCarousel'

const TrainingComplete: FC = () => {
  const { token } = useParams()
  const { t } = useTranslation()
  const queryParams = useUrlSearchQuery()
  const { data } = useVideosTraining(token)
  const queryClient = useQueryClient()

  const deviceType = useDeviceDetection()

  // Overwrite the query data with modified completed status
  useEffect(() => {
    if (data && queryParams.index) {
      const modifiedData = {
        ...data,
        videos: data.videos.map((video) => ({
          ...video,
          completed: video.completed || video.id === queryParams.index,
        })),
      }

      queryClient.setQueryData(['video-trainings', token], modifiedData)
    }
  }, [data, queryParams.index, token, queryClient])

  const videosTrainingData = data

  const isPackage = useMemo(() => {
    return (videosTrainingData?.videos?.length ?? 0) > 1
  }, [videosTrainingData])

  const isPackageCompleted = useMemo(() => {
    if (!isPackage) return false
    const completedVideos = videosTrainingData?.videos.reduce((acc, video) => {
      if (video.completed) {
        return acc + 1
      }
      return acc
    }, 0)

    return completedVideos === videosTrainingData?.videos.length
  }, [videosTrainingData])

  const { LottieAnimation } = useLottie({
    animationData: trainingCompleteAnimation,
    height: 240,
    width: 240,
    options: {
      loop: false,
      autoplay: true,
      animationData: isPackageCompleted ? trainingPackageCompleteAnimation : trainingCompleteAnimation,
      rendererSettings: {
        preserveAspectRatio: 'xMidYMid slice',
      },
    },
  })

  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: ['video-trainings'] })
  }, [])

  return (
    <StyledContainer>
      <Box padding={4}>
        <img alt={'Cywareness Logo'} src={LogoText} height={deviceType === DeviceType.Mobile ? 48 : 72} />
      </Box>
      <StyledPattayaTypography>
        {isPackageCompleted ? t('trainingPackageCompleted.title') : t('trainingCompleted.title')}
      </StyledPattayaTypography>
      <Box>{LottieAnimation}</Box>
      <Typography fontSize={20} fontWeight="fontWeightMedium">
        {t('trainingCompleted.description')}
        <br />
        {t('trainingCompleted.beAware')}
      </Typography>
      {!isPackage && (
        <Box width="156px" padding={theme.spacing(1, 0)}>
          <Button
            size="large"
            fullWidth
            variant="contained"
            onClick={() => {
              window.close()
            }}>
            {t('trainingCompleted.closeTab')}
          </Button>
        </Box>
      )}

      {isPackage && (
        <Box>
          <Divider sx={{ borderBottomWidth: '4px' }} />
          <VideosCarousel
            videos={videosTrainingData?.videos ?? []}
            currentVideoId={queryParams.index}
            deviceType={deviceType}
          />
        </Box>
      )}
      <Typography fontWeight="fontWeightMedium">
        {
          <Trans
            i18nKey={'trainingCompleted.learnMore'}
            components={{
              1: (
                <Link
                  to={'https://cywareness.io/'}
                  style={{
                    color: theme.palette.blue[900],
                  }}
                />
              ),
            }}
          />
        }
      </Typography>
    </StyledContainer>
  )
}

const StyledContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flexDirection: 'column',
  textAlign: 'center',
  gap: theme.spacing(2),
  padding: theme.spacing(3),
  minHeight: '100vh',
})

const StyledPattayaTypography = styled(Typography)({
  fontSize: 48,
  fontWeight: theme.typography.fontWeightRegular,
  fontFamily: 'Pattaya',
  background: `linear-gradient(${theme.palette.blueGray[200]}, ${theme.palette.blueDianne[900]} ,${theme.palette.grey[900]})`,
  WebkitTextFillColor: 'transparent',
  WebkitBackgroundClip: 'text',
  paddingRight: theme.spacing(2),
})

export default TrainingComplete
